import { useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "react-query";
import toast from "react-hot-toast";
import { Message, ChatInitialState } from "../interfaces/chat.interface";
import { ConversationType } from "../constants/chat.constant";
import useChatApiService from "../services/chat.service";
import useChatLocalService from "../services/chat-local.service";
import { Citation } from "../constants/citation.constant";
import { ChatUtils } from "@/services/chat-utils.service";

/**
 * useChatEffects hook manages side effects for chat including URL synchronization,
 * conversation loading, folder fetching, and citation window setup. Handles
 * conversation selection from URL params and merges server/local conversations.
 * 
 * @hook
 * @param {any} chatState - Chat state from useChatState hook
 * @param {ChatInitialState} state - Global chat state from context
 * @param {Function} dispatch - Context dispatch function
 * @param {ConversationType} conversationType - Current conversation type
 * @param {(type: ConversationType) => void} setConversationType - Function to change conversation type
 * @param {(citation: Citation) => void} openCitationDetails - Function to open citation details
 * @param {() => void} closeSources - Function to close sources modal
 * @returns {Object} Effect results
 * @returns {any} returns.chatFolderDetails - Fetched chat folder details
 * @returns {Function} returns.refetchFolders - Function to refetch folders
 * @returns {boolean} returns.isChatHistoryLoading - Whether chat history is loading
 * @returns {Function} returns.urlTypeToEnum - Function to convert URL type to enum
 * 
 * @example
 * ```tsx
 * const {
 *   chatFolderDetails,
 *   refetchFolders,
 *   isChatHistoryLoading
 * } = useChatEffects(
 *   chatState,
 *   state,
 *   dispatch,
 *   conversationType,
 *   setConversationType,
 *   openCitationDetails,
 *   closeSources
 * );
 * ```
 */
export const useChatEffects = (
  chatState: any,
  state: ChatInitialState,
  dispatch: any,
  conversationType: ConversationType,
  setConversationType: (type: ConversationType) => void,
  openCitationDetails: (citation: Citation) => void,
  closeSources: () => void
) => {
  const { type, conversationId } = useParams<{
    type: string;
    conversationId?: string;
  }>();
  const navigate = useNavigate();
  
  const { getSelectedConversation, saveSelectedConversation } = useChatLocalService();
  const { getAllChatFolders, getChatConversation } = useChatApiService();

  const urlTypeToEnum = useCallback((urlType: string): ConversationType => {
    const formatted = urlType.toUpperCase().replace(/-/g, "_");
    return Object.values(ConversationType).includes(
      formatted as ConversationType
    )
      ? (formatted as ConversationType)
      : ConversationType.CHOP_GPT;
  }, []);

  // Citation window setup
  useEffect(() => {
    window.openCitationDetails = (citation: Citation) => {
      openCitationDetails(citation);
    };

    return () => {
      delete window.openCitationDetails;
    };
  }, [openCitationDetails]);

  // URL type change effect
  useEffect(() => {
    if (type) {
      const enumType = urlTypeToEnum(type);

      if (enumType !== conversationType) {
        setConversationType(enumType);
      }
    }
  }, [type, conversationType, setConversationType]);

  // Chat history loading effect
  const {
    data: chatFolderDetails,
    refetch: refetchFolders,
    isLoading: isChatHistoryLoading,
    isError,
    error,
  } = useQuery(
    ["GetChatFolderDetails", conversationType],
    () => getAllChatFolders(conversationType),
    {
      enabled: true,
      refetchOnMount: true,
    }
  );


  useEffect(() => {
    dispatch({
      field: "isLoadingHistory",
      value: isChatHistoryLoading,
    });
  }, [isChatHistoryLoading]);

  useEffect(() => {
    dispatch({ field: "refetchFolders", value: refetchFolders });
  }, [refetchFolders]);
  

  useEffect(() => {
    const newType = type ? ChatUtils.urlTypeToEnum(type) : ConversationType.CHOP_GPT;
    dispatch({ field: "conversationType", value: newType });
  }, [type]);

  useEffect(() => {
    if (chatFolderDetails && chatFolderDetails.data) {
      const serverConversationIds = new Set(
        chatFolderDetails.data.map(conv => conv.session_id)
      );

      const localConversations = state.conversations.filter(conv => {
        if (conv.conversation_type !== conversationType) return false;
        if (serverConversationIds.has(conv.session_id)) return false;
        const status = state.conversationStatusMap.get(conv.session_id);
        return status === false;
      });

      const mergedConversations = [...chatFolderDetails.data, ...localConversations];
      
      // Sort conversations by date (most recent first)
      const sortedConversations = mergedConversations.sort((a, b) => {
        const aDate = a.updated_at || a.created_at;
        const bDate = b.updated_at || b.created_at;

        if (aDate && bDate) {
          return new Date(bDate).getTime() - new Date(aDate).getTime();
        }
        
        if (!aDate && bDate) return -1;
        if (aDate && !bDate) return 1;
        return 0;
      });
      dispatch({ field: "conversations", value: sortedConversations });
    } else if (chatFolderDetails && Array.isArray(chatFolderDetails.data) && chatFolderDetails.data.length === 0) {
      const filteredConversations = state.conversations.filter(
        conv => conv.conversation_type !== conversationType
      );
      dispatch({ field: "conversations", value: filteredConversations });
    }
  }, [chatFolderDetails, conversationType, state.conversations, state.conversationStatusMap, dispatch])

  useEffect(() => {
    if (!chatFolderDetails || isChatHistoryLoading) return;

    if (chatFolderDetails && chatFolderDetails.data) {
      let parsedConversations = [...chatFolderDetails.data];


      if (conversationId) {
        const conversationFromUrl = parsedConversations.find(
          (conv) =>
            conv.session_id === conversationId &&
            conv.conversation_type === conversationType
        );

        if (conversationFromUrl) {
          if (
            state.selectedConversation?.session_id !==
            conversationFromUrl.session_id
          ) {
            dispatch({
              field: "selectedConversation",
              value: conversationFromUrl,
            });

            const newStatusMap = new Map(state.conversationStatusMap);
            newStatusMap.set(conversationFromUrl.session_id, true);
            dispatch({ field: "conversationStatusMap", value: newStatusMap });

            saveSelectedConversation(
              conversationFromUrl.session_id,
              conversationType
            );
          } else if (
            state.selectedConversation?.session_id === conversationFromUrl.session_id &&
            state.selectedConversation?.sensitive !== conversationFromUrl.sensitive
          ) {
            dispatch({
              field: "selectedConversation",
              value: {
                ...state.selectedConversation,
                sensitive: conversationFromUrl.sensitive || false,
              },
            });
          }
          return;
        } else {
          const localConversation = state.conversations.find(
            (conv) =>
              conv.session_id === conversationId &&
              conv.conversation_type === conversationType
          );

          if (localConversation) {
            dispatch({
              field: "selectedConversation",
              value: localConversation,
            });

            const newStatusMap = new Map(state.conversationStatusMap);
            newStatusMap.set(localConversation.session_id, false);
            dispatch({ field: "conversationStatusMap", value: newStatusMap });

            saveSelectedConversation(
              localConversation.session_id,
              conversationType
            );
          } else {
            // Only navigate away if there's no currently selected conversation
            // This prevents URL blinking when creating new conversations
            if (!state.selectedConversation || state.selectedConversation.session_id !== conversationId) {
              const urlType = conversationType.toLowerCase().replace(/_/g, "-");
              navigate(`/chat/${urlType}`, { replace: true });
              dispatch({
                field: "selectedConversation",
                value: null,
              });
            }
          }
        }
      } else {
        dispatch({
          field: "selectedConversation",
          value: null,
        });
      }
    }

    if (isError && !isChatHistoryLoading) {
      toast.error(error ? (error as string) : "Something went wrong.");
    }
  }, [chatFolderDetails, conversationType, conversationId, dispatch, state, navigate, saveSelectedConversation, isError, isChatHistoryLoading, error]);

  return {
    chatFolderDetails,
    refetchFolders,
    isChatHistoryLoading,
    urlTypeToEnum,
  };
};